import React, { useState, useEffect } from 'react';
import Glitch from 'glitch-javascript-sdk';
import Navigate from '../../../../util/Navigate';
import { useNavigate } from 'react-router-dom';

const PublisherOnboardinStep4Page = () => {
    const [community, setCommunity] = useState({});
    const [activeTab, setActiveTab] = useState('quickActions');
    const navigate = useNavigate();

    const handleCreateCampaign = () => navigate(Navigate.campaignsCreatePage());
    const handleCreateCommunity = () => navigate(Navigate.communitiesGettingStartedPage(community.id));
    const handleNewsletter = () => navigate(Navigate.newsletterCreatePage());
    const handleMediakit = () => navigate(Navigate.mediakitsPublishersCreatePage());
    const handleTesters = () => navigate(Navigate.playtestingPublishersListPage());
    const handleAds = () => navigate(Navigate.adsCampaignsListPage());
    const handleSocial = () => navigate(Navigate.schedulerCreatePage());
    const handleAnalytics = () => navigate(Navigate.reportsMainPage());
    const handleChat = () => navigate(Navigate.titlesChatPage());

    useEffect(() => {
        Glitch.api.Communities.list({ roles: [1, 2], order_by: 'created_at' })
            .then(response => {
                if (response.data.data.length > 0) {
                    setCommunity(response.data.data[0]);
                }
            })
            .catch(error => { });
    }, []);

    const actionCards = [
        {
            title: "AI Chat",
            icon: "fa-comments",
            handler: handleChat,
            description: "Get instant help from our AI assistant"
        },
        {
            title: "Advertise",
            icon: "fa-vial",
            handler: handleAds,
            description: "Start advertising your game"
        },
        {
            title: "Access Analytics",
            icon: "fa-chart-pie",
            handler: handleAnalytics,
            description: "View your game's performance metrics"
        },
        {
            title: "Automate Social Media",
            icon: "fa-thumbs-up",
            handler: handleSocial,
            description: "Automate social posts across platforms"
        },
        {
            title: "Find Influencers",
            icon: "fa-bullhorn",
            handler: handleCreateCampaign,
            description: "Launch an influencer campaign for your game"
        },
        {
            title: "Start A Newsletter",
            icon: "fa-newspaper",
            handler: handleNewsletter,
            description: "Engage your audience via email"
        },
        {
            title: "Create A Media Kit",
            icon: "fa-photo-video",
            handler: handleMediakit,
            description: "Share assets with press & creators"
        },
    ];

    return (
        <div className="container-fluid px-3 py-4">
            {/* Hero Section */}
            <div className="text-center mb-5">
                <div className="badge bg-light text-dark mb-3 p-2">Step 4 of 4</div>
                <h1 className="display-5 fw-bold mb-3">You're Done! <br />What would you like to do first? 🎊</h1>
                <p className="lead mx-auto" style={{ maxWidth: '700px' }}>
                    Congratulations on completing your registration! Select what you want to do first our connect with our chat bot to help guide you.
                </p>
            </div>

            {/* Tab Navigation */}
            <ul className="nav nav-pills justify-content-center mb-4">
                <li className="nav-item">
                    <button 
                        className={`nav-link ${activeTab === 'quickActions' ? 'active bg-danger' : 'text-white'}`}
                        onClick={() => setActiveTab('quickActions')}
                    >
                        Quick Actions
                    </button>
                </li>
                <li className="nav-item ms-3">
                    <button 
                        className={`nav-link ${activeTab === 'learnMore' ? 'active bg-danger' : 'text-white'}`}
                        onClick={() => setActiveTab('learnMore')}
                    >
                        Learn More
                    </button>
                </li>
            </ul>

            {/* Tab Content */}
            <div className="tab-content container">
                {activeTab === 'quickActions' && (
                    <div className="tab-pane fade show active">
 
                        
                        <div className="row g-4 justify-content-center">
                            {actionCards.map((action, index) => (
                                <div key={index} className="col-xl-3 col-lg-4 col-md-6 col-sm-6 col-12">
                                    <div 
                                        className="card h-100 cursor-pointer transition-all hover-scale bg-white text-black border border-secondary"
                                        onClick={action.handler}
                                        style={{ 
                                            transition: 'transform 0.2s, box-shadow 0.2s',
                                            minHeight: '200px'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
                                        onMouseLeave={(e) => e.currentTarget.style.transform = ''}
                                    >
                                        <div className="card-body text-center p-4 d-flex flex-column">
                                            <div className="mb-3">
                                                <i className={`fas fa-2x text-danger ${action.icon}`}></i>
                                            </div>
                                            <h5 className="card-title mb-2">{action.title}</h5>
                                            <p className="card-text small mb-3">{action.description}</p>
                                            <div className="mt-auto">
                                                <span className="btn btn-sm btn-danger rounded-pill px-3">
                                                    Get Started <i className="fas fa-arrow-right ms-1"></i>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                
                {activeTab === 'learnMore' && (
                    <div className="tab-pane fade show active">
                        <div className="row justify-content-center">
                            <div className="col-lg-8 col-md-10">
                                <div className="card border-0 bg-dark">
                                    <div className="card-body p-4 p-md-5">
                                        <h4 className="mb-4">Getting Started Guide</h4>
                                        
                                        <div className="mb-4">
                                            <h5>Influencer Campaigns</h5>
                                            <p className="text-white-50">
                                                Find and collaborate with influencers to market your game. 
                                                Our platform helps you identify the right creators and manage 
                                                your campaigns efficiently.
                                            </p>
                                        </div>
                                        
                                        <div className="mb-4">
                                            <h5>Community Building</h5>
                                            <p className="text-white-50">
                                                Create a dedicated space for your fans with forums, announcements, 
                                                and engagement tools. Perfect for building long-term relationships 
                                                with your player base.
                                            </p>
                                        </div>
                                        
                                        <div className="mb-4">
                                            <h5>Playtesting</h5>
                                            <p className="text-white-50">
                                                Connect with testers who can provide valuable feedback on your game 
                                                during development. Get detailed reports to improve your product.
                                            </p>
                                        </div>
                                        
                                        <div className="mb-4">
                                            <h5>Media Kits & Newsletters</h5>
                                            <p className="text-white-50">
                                                Professional tools to share your game with press and content creators, 
                                                and keep your audience updated with regular newsletters.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Bottom CTA */}
            <div className="text-center mt-5 pt-3">
                <p className="text-white-50 mb-3">Not sure where to start?</p>
                <button 
                    className="btn btn-danger px-4 py-2 rounded-pill"
                    onClick={handleChat}
                >
                    <i className="fas fa-comments me-2"></i> Chat with our AI Assistant
                </button>
            </div>
        </div>
    );
};

export default PublisherOnboardinStep4Page;